import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../../utils/api';
import QRCode from 'qrcode';

const POLL_INTERVAL_MS = 5000;

const TILES = [
  { to: '/owner/orders', icon: '📦', label: 'Order Queue', badgeKey: 'waitingCount' },
  { to: '/owner/menu', icon: '📋', label: 'Menu', badgeKey: null },
  { to: '/owner/sales', icon: '💰', label: 'Sales', badgeKey: null },
  { to: '/owner/rewards', icon: '🎁', label: 'Rewards', badgeKey: null },
  { to: '/owner/settings', icon: '⚙️', label: 'Settings', badgeKey: null },
  { to: '/owner/theme', icon: '🎨', label: 'Theme', badgeKey: null },
  { action: 'qr', icon: '📱', label: 'My QR', badgeKey: null },
];

export default function Dashboard() {
  const { getToken } = useAuth();
  const location = useLocation();
  const prefix = location.pathname.startsWith('/__test__') ? '/__test__' : '';
  const [business, setBusiness] = useState(null);
  const [orders, setOrders] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    if (!silent) setError(null);
    try {
      const [meRes, ordersRes] = await Promise.all([
        api('/api/owners/me', { getToken }),
        api('/api/owners/me/orders', { getToken }),
      ]);
      if (!meRes.ok) {
        const errData = await meRes.json().catch(() => ({}));
        throw new Error(errData.error || `Failed to load: ${meRes.status}`);
      }
      const meData = await meRes.json();
      setBusiness(meData.business);
      if (ordersRes.ok) {
        const ordersData = await ordersRes.json();
        setOrders(ordersData);
      }
    } catch (err) {
      if (!silent) setError(err.message || 'Failed to load dashboard.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(true), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchData]);

  const waitingCount = orders?.waitingCount ?? orders?.orders?.filter((o) => ['pending', 'paid'].includes(o.status)).length ?? 0;

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4">
        <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-slate-600 dark:text-slate-400">Loading dashboard…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6">
        <p className="text-red-700 dark:text-red-300 font-medium">{error}</p>
        <button
          type="button"
          onClick={fetchData}
          className="mt-4 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
          Owner Dashboard
        </h1>
        <p className="mt-2 text-slate-600 dark:text-slate-400">
          Manage your menu and view orders.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {TILES.map((tile) => {
          const badge = tile.badgeKey === 'waitingCount' && waitingCount > 0 ? waitingCount : null;
          if (tile.action === 'qr') {
            return (
              <button
                key="qr"
                type="button"
                onClick={() => setQrOpen(true)}
                className="relative rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-amber-500 hover:bg-amber-50/30 dark:hover:bg-amber-900/10 p-6 min-h-[100px] flex flex-col items-center justify-center text-center"
              >
                <span className="text-2xl">{tile.icon}</span>
                <span className="mt-2 font-medium text-slate-900 dark:text-slate-100">{tile.label}</span>
              </button>
            );
          }
          return (
            <Link
              key={tile.to}
              to={prefix + tile.to}
              className="relative rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-amber-500 hover:bg-amber-50/30 dark:hover:bg-amber-900/10 p-6 min-h-[100px] flex flex-col items-center justify-center text-center"
            >
              {badge != null && (
                <span className="absolute top-2 right-2 flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full text-xs font-medium bg-amber-500 text-slate-900">
                  {badge}
                </span>
              )}
              <span className="text-2xl">{tile.icon}</span>
              <span className="mt-2 font-medium text-slate-900 dark:text-slate-100">{tile.label}</span>
            </Link>
          );
        })}
      </div>

      <Link
        to={prefix + '/owner/scan'}
        className="flex w-full items-center justify-center py-5 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-amber-500 hover:bg-amber-50/30 dark:hover:bg-amber-900/10 px-6 font-medium text-slate-900 dark:text-slate-100 min-h-[60px]"
      >
        📷 Scan order QR
      </Link>

      {qrOpen && (
        <MyQRModal
          slug={business?.slug}
          businessName={business?.name}
          onClose={() => setQrOpen(false)}
        />
      )}
    </div>
  );
}

function MyQRModal({ slug, businessName, onClose }) {
  const [qrDataUrl, setQrDataUrl] = useState(null);

  const menuUrl = slug ? `${window.location.origin}/m/${slug}` : '';

  useEffect(() => {
    if (!menuUrl) return;
    QRCode.toDataURL(menuUrl, { width: 256, margin: 2 }).then(setQrDataUrl);
  }, [menuUrl]);

  const handleDownload = async () => {
    if (!menuUrl) return;
    try {
      const url = await QRCode.toDataURL(menuUrl, { width: 512, margin: 2 });
      const a = document.createElement('a');
      a.href = url;
      a.download = `menu-qr-${slug || 'menu'}.png`;
      a.click();
    } catch (e) {
      console.warn('Download failed', e);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(menuUrl);
      alert('Link copied to clipboard');
    } catch {
      alert('Could not copy link');
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-50" onClick={onClose} aria-hidden />
      <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 max-w-sm mx-auto bg-white dark:bg-slate-800 rounded-2xl shadow-xl z-50 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            My QR · {businessName || 'Menu'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600"
          >
            ✕
          </button>
        </div>
        {qrDataUrl && (
          <div className="flex justify-center mb-4">
            <img src={qrDataUrl} alt="Menu QR" className="w-64 h-64" />
          </div>
        )}
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 break-all">
          {menuUrl || 'Set up your business slug in Settings'}
        </p>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleDownload}
            disabled={!menuUrl}
            className="flex-1 py-3 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-slate-900 font-medium"
          >
            Download QR
          </button>
          <button
            type="button"
            onClick={handleCopyLink}
            disabled={!menuUrl}
            className="flex-1 py-3 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 font-medium"
          >
            Copy link
          </button>
        </div>
      </div>
    </>
  );
}
